import { yupResolver } from "@hookform/resolvers/yup"
import { Typography } from "@mui/material"
import { useMutation, useQuery } from "@tanstack/react-query"
import {
  getLostFoundTrip,
  LostFoundColorPublicResponse,
  LostFoundContentPublicResponse,
  LostFoundCreateCasePublicRequest,
  postLostFoundCreateCase,
} from "api"
import "dayjs/locale/he"
import { useTranslations } from "next-intl"
import { Dispatch, LegacyRef, SetStateAction, useRef, useState } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import { useForm } from "react-hook-form"
import { MarketingConsent } from "ui"
import {
  applicationRequestError,
  applicationRequestSuccess,
  useFocusOnError,
} from "utils"
import { commonSchema, messagesInvalid } from "utils/FormSchema"
import * as yup from "yup"
import {
  CustomAutocomplete,
  CustomCheckbox,
  CustomSelect,
  CustomTextField,
  DateController,
  formatDate,
  Header,
  isFieldRequired,
  MultilineCustomTextField,
  notAfterNowIfToday,
  NumbersController,
  prefixOptionsArray,
  RECAPTCHA_KEY,
} from "./Common"
import { FormContainer, InputsGrid, StyledButton } from "./styles"

export default function LostFound({
  handleResult,
  locale,
  lostFoundContentData,
  lostFoundColorsData,
  setIsLoading,
  label,
  tooltipText,
}: {
  handleResult: (result: "yes" | "no", id?: string) => void
  locale: "he" | "en" | "ar" | "ru"
  lostFoundContentData: LostFoundContentPublicResponse[]
  lostFoundColorsData: LostFoundColorPublicResponse[]
  setIsLoading: Dispatch<SetStateAction<boolean>>
  label: string
  tooltipText: string
}) {
  const [isPending, setIsPending] = useState(false)
  const t = useTranslations("Forms")
  const tInner = useTranslations("InnerPages")
  const [shilut, setShilut] = useState<string>()
  const reCaptchaRef = useRef<ReCAPTCHA>(null)
  const schema = yup.object({
    firstName: commonSchema.common(locale).firstName,
    lastName: commonSchema.common(locale).lastName,
    // email: commonSchema.common(locale).email,
    cellphone1: commonSchema.common(locale).phonePrefix,
    phone1: commonSchema
      .common(locale)
      .phone.max(7, messagesInvalid[locale].phoneNumber),
    cellphone2: commonSchema.common(locale).phonePrefix.optional(),
    phone2: commonSchema
      .common(locale)
      .phone.max(7, messagesInvalid[locale].phoneNumber)
      .optional(),
    contentId: commonSchema.common(locale).lostContentId,
    identificationValue: yup
      .string()
      .max(30, messagesInvalid[locale].identificationValue)
      .test(
        "identificationValueRequired",
        messagesInvalid[locale].identificationValue,
        function (value) {
          const { contentId } = this.parent as any
          const selected = lostFoundContentData?.find(
            (el) => el.id === contentId,
          )
          if (selected?.callCenter === 1) {
            return !!value
          }
          return true
        },
      ),
    makat8: yup.string().required(messagesInvalid[locale].makat),
    colorId: commonSchema.common(locale).lostColorId,
    brandName: commonSchema.common(locale).brandName,
    lostDate: commonSchema.common(locale).eventDate,
    boardingDt: commonSchema
      .common(locale)
      .exactEventTime.test(
        "notAfterNowIfToday",
        messagesInvalid[locale].timeNotInFuture,
        function (value) {
          const { lostDate } = this.parent
          return notAfterNowIfToday(value, lostDate)
        },
      ),
    ravKavAccount: commonSchema.common(locale).ravkav,
    content: commonSchema.common(locale).inquiryDescription,
    kosherPhone: commonSchema.common(locale).kosherPhone,
    mailingAcceptance: yup.boolean().optional(),
  })
  type FormData = yup.InferType<typeof schema>

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitted, isValid },
    watch,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      cellphone1: prefixOptionsArray[0].value,
      cellphone2: prefixOptionsArray[0].value,
    },
  })

  // Add focus management for validation errors
  useFocusOnError(errors, isSubmitted)
  const contentId = watch("contentId")
  const lostDate = watch("lostDate")
  const selectedContent = lostFoundContentData?.find(
    (el) => el.id === contentId,
  )
  const showIdentificationValue = selectedContent?.callCenter === 1
  const { mutate } = useMutation({
    mutationFn: postLostFoundCreateCase,
    onMutate: () => {
      setIsLoading(true)
    },
    onSuccess: (data) => {
      if (data.data.errorCode) {
        applicationRequestError(tInner("submitInquiry"), label)
        handleResult("yes")
      } else {
        const id = data.data.body.id
        applicationRequestSuccess(tInner("submitInquiry"), label)
        handleResult("no", String(id))
      }
    },
    onError: () => {
      applicationRequestError(tInner("submitInquiry"), label)
      handleResult("yes")
    },
    onSettled: () => {
      setIsLoading(false)
      setIsPending(false)
    },
  })
  const { data, isLoading } = useQuery({
    queryKey: ["lostFoundTrip", lostDate, shilut],
    queryFn: async () => {
      try {
        if (!shilut || !lostDate) return []
        const res = await getLostFoundTrip(
          Number(shilut),
          lostDate.toISOString(),
        )
        return res.data.body || []
      } catch (error) {
        console.log(error)
        return []
      }
    },
  })
  // 2203
  const onSubmit = async (data: FormData) => {
    setIsPending(true)
    const eventDate = formatDate(data.lostDate)
    reCaptchaRef?.current?.reset()
    const recaptchaResponse =
      (await reCaptchaRef?.current?.executeAsync()) as string
    const phone2 = data.phone2 ? data.cellphone2 + data.phone2! : null
    const formattedData: LostFoundCreateCasePublicRequest = {
      boardingDt: data.boardingDt,
      colorId: data.colorId,
      contentId: data.contentId,
      firstName: data.firstName,
      lastName: data.lastName,
      phone: data.cellphone1 + data.phone1,
      recaptchaResponse,
      lostDate: eventDate,
      makat8: data.makat8,
      description: data.content,
      ravKav: data.ravKavAccount,
      identificationValue: data.identificationValue,
      identificationTypeId: !!data.identificationValue ? 1 : 0,
      brand: data.brandName,
      noSms: data.kosherPhone || false,
      phone2,
      sid: window?.__ctm?.config?.sid || "-1",
      mailingAcceptance: data.mailingAcceptance ? 1 : 0,
    } as any

    mutate(formattedData)
  }
  return (
    <FormContainer onSubmit={handleSubmit(onSubmit)}>
      <Header />
      <InputsGrid>
        <CustomTextField
          name="firstName"
          control={control}
          label={t("firstName")}
          required={isFieldRequired(schema, "firstName")}
          isSubmitted={isSubmitted}
          errors={errors}
        />
        <CustomTextField
          name="lastName"
          control={control}
          label={t("lastName")}
          required={isFieldRequired(schema, "lastName")}
          isSubmitted={isSubmitted}
          errors={errors}
        />
        {/* <CustomTextField
          name="email"
          control={control}
          label={t("email")}
          required={isFieldRequired(schema, "email")}
          isSubmitted={isSubmitted}
          errors={errors}
        /> */}

        <NumbersController
          prefixName="cellphone1"
          phoneName="phone1"
          control={control}
          label={t("mobileNumber")}
          required={isFieldRequired(schema, "phone1")}
          isSubmitted={isSubmitted}
          errors={errors}
          locale={locale}
        />
        <NumbersController
          prefixName="cellphone2"
          phoneName="phone2"
          control={control}
          label={t("secondMobileNumber")}
          required={isFieldRequired(schema, "phone2")}
          isSubmitted={isSubmitted}
          errors={errors}
          locale={locale}
        />
        <CustomCheckbox
          errors={errors}
          name="kosherPhone"
          control={control}
          label={t("kosherPhone")}
        />
      </InputsGrid>
      <Typography variant="h4">{t("lostItemDetails")}</Typography>
      <InputsGrid>
        <CustomSelect
          control={control}
          label={t("lostItem")}
          name="contentId"
          options={lostFoundContentData?.map((el) => ({
            value: el.id,
            label: el.name,
          }))}
          errors={errors}
          isSubmitted={isSubmitted}
          required={isFieldRequired(schema, "contentId")}
          fullWidth
        />
        {showIdentificationValue && (
          <CustomTextField
            name="identificationValue"
            control={control}
            label={selectedContent.identificationValueDisplayText || ""}
            required={true}
            isSubmitted={isSubmitted}
            errors={errors}
          />
        )}
        <CustomSelect
          control={control}
          label={t("lostColor")}
          name="colorId"
          options={lostFoundColorsData.map((el) => ({
            value: el.id,
            label: el.name,
          }))}
          errors={errors}
          isSubmitted={isSubmitted}
          required={isFieldRequired(schema, "colorId")}
          fullWidth
        />
        <CustomTextField
          name="brandName"
          control={control}
          label={t("lostBrandName")}
          required={isFieldRequired(schema, "brandName")}
          isSubmitted={isSubmitted}
          errors={errors}
        />
        <DateController
          name="lostDate"
          control={control}
          label={t("dateOfLoss")}
          required={isFieldRequired(schema, "lostDate")}
          isSubmitted={isSubmitted}
          errors={errors}
          midnightText={tInner("afterMidnight")}
          okButtonLabel={tInner("okButtonLabel")}
          cancelButtonLabel={tInner("cancelButtonLabel")}
          toolbarTitle={tInner("toolbarTitle")}
          locale={locale}
        />
        <CustomTextField
          name="boardingDt"
          control={control}
          label={t("timeToBoarding")}
          required={isFieldRequired(schema, "boardingDt")}
          isSubmitted={isSubmitted}
          errors={errors}
        />
        <CustomTextField
          name="ravKavAccount"
          control={control}
          label={t("ravKavCardLost")}
          required={isFieldRequired(schema, "ravKavAccount")}
          isSubmitted={isSubmitted}
          errors={errors}
        />
        <CustomAutocomplete
          control={control}
          label={t("lineNumber")}
          name="makat8"
          onInputChange={(value) => {
            setShilut(value)
          }}
          required={isFieldRequired(schema, "makat8")}
          disabled={!lostDate}
          options={
            data?.map((el) => ({
              label: `קו ${shilut} מתחנת ${el.originYeshuvName} לתחנת ${el.destYeshuvName}`,
              value: el.eggedMakat8,
            })) || []
          }
          loading={isLoading}
          errors={errors}
          isSubmitted={isSubmitted}
          loadingText={tInner("loadingText")}
          noOptionsText={tInner("noOptionsText")}
          startTypingText={messagesInvalid[locale].makat}
        />
      </InputsGrid>

      <MultilineCustomTextField
        name="content"
        control={control}
        label={t("lostItemDescription")}
        required={isFieldRequired(schema, "content")}
        isSubmitted={isSubmitted}
        errors={errors}
      />
      <MarketingConsent
        name="mailingAcceptance"
        control={control}
        tooltipText={tooltipText}
      />
      <StyledButton variant="primary" type="submit" disabled={isPending}>
        {tInner("submitInquiry")}
      </StyledButton>
      {RECAPTCHA_KEY && (
        <ReCAPTCHA
          ref={reCaptchaRef as LegacyRef<ReCAPTCHA>}
          sitekey={RECAPTCHA_KEY}
          size="invisible"
          hl="iw"
          tabIndex={-1}
        />
      )}
    </FormContainer>
  )
}
